import { useRateCard } from "./shell-hooks";

/**
 * What one metered action costs in credits.
 *
 * The server owns this and now says so: `GET /v1/rate-card` serves the active
 * card — whole credits per unit of quantity, per meter, with the version those
 * figures came from. One card for everybody, not grandfathered per account, and
 * a staff change lands on everyone at once with no undo (`decisions/0025`
 * decision 4). So there is no copy of any price in this repository, and the last
 * one was deleted the day the route arrived: a figure this side that staff can
 * change on the other is a price that goes quietly wrong, and it goes wrong
 * silently on the one screen where being wrong costs a studio money.
 *
 * That is also the whole of what decision 5 traded the issuance allowance for —
 * "the price being low and visible at the point of the action". Visible means
 * read from the thing that will charge it.
 */

/**
 * The meters, closed.
 *
 * `metering/api`'s own set, and closed here for its reason: "a meter is a price.
 * An open string would let a caller invent a meter with no rate, which records
 * an amount of zero and looks exactly like nothing happening." A charging
 * surface naming a meter this does not have is a type error rather than a
 * control that says an action is free.
 *
 * The card's own `rates` is an open map on the wire, deliberately — a meter
 * retired or added since a build shipped must not fail to parse. The closed set
 * is on the asking side, which is where inventing a meter would do harm.
 */
export type Meter =
  "ai_call" | "ai_djinn_answer" | "ai_djinn_fix" | "simulation" | "redemption" | "tool_call";

/**
 * The rate for one meter, or undefined while nobody has said what it is.
 *
 * Three states and they are three different things:
 *
 *   - a number, which is the active card's figure for this meter;
 *   - **nought**, when the card does not price this meter — the card's own
 *     decision, since publishing a card is how a price is set, and the reading
 *     the server takes when it charges;
 *   - **undefined**, when the card has not arrived or could not be read. Not
 *     nought, and this is the distinction the whole file turns on: a control
 *     that reads "0 credits" because a fetch is in flight is a control quoting a
 *     price nobody agreed to, and it is pressable.
 *
 * There is no organisation in it. The card is the platform's own list and is the
 * same figure whoever asks, so a rate is not a property of who is looking — and
 * one query on one key serves every charging control on a screen.
 */
export function useMeterRate(meter: Meter): number | undefined {
  const card = useRateCard();
  if (!card.data) {
    return undefined;
  }
  return card.data.rates[meter] ?? 0;
}
